import React from 'react'
import { formatCurrencyAmount, getCurrencySymbol } from '../../utils/currencyUtils'
import {
	Box,
	Typography,
	Button,
	useMediaQuery,
	useTheme
} from '@mui/material'
import StripeCheckout from '../StripeCheckout'
import { buildFullName } from '../../utils/clientUtils'

// Main PaymentStep Component
function PaymentStep({
	merchant,
	clientInfo,
	petServices,
	services,
	addOns,
	onPaymentSuccess,
	onPaymentError,
	onBack
}) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	const formatCurrency = (cents) => formatCurrencyAmount(cents, merchant?.currency)

	// Calculate total service price (services + add-ons)
	const calculateTotalPrice = () => {
		let total = 0
		petServices.forEach(ps => {
			// Add service price
			const service = services.find(s => s.id === ps.serviceId)
			if (service) {
				total += service.price || 0
			}

			// Add add-ons prices
			(ps.addOnIds || []).forEach(addOnId => {
				const addOn = addOns.find(a => a.id === addOnId)
				if (addOn) {
					total += addOn.price || 0
				}
			})
		})
		return total
	}

	// Format late cancel fee display
	const formatLateCancelFee = () => {
		if (!merchant?.late_cancel_fee_type || merchant?.late_cancel_fee_value == null) {
			return null
		}

		const totalPrice = calculateTotalPrice()

		if (merchant.late_cancel_fee_type === 'percentage') {
			const feeAmount = (totalPrice * merchant.late_cancel_fee_value) / 100
			return `${merchant.late_cancel_fee_value}% of service price (${formatCurrency(feeAmount)})`
		} else {
			return formatCurrency(merchant.late_cancel_fee_value)
		}
	}

	// Format no-show fee display
	const formatNoShowFee = () => {
		// First check for explicit no-show fee
		if (merchant?.no_show_fee_type && merchant?.no_show_fee_value != null) {
			const totalPrice = calculateTotalPrice()

			if (merchant.no_show_fee_type === 'percentage') {
				const feeAmount = (totalPrice * merchant.no_show_fee_value) / 100
				return `${merchant.no_show_fee_value}% of service price (${formatCurrency(feeAmount)})`
			} else {
				return formatCurrency(merchant.no_show_fee_value)
			}
		}

		// Fall back to late cancel fee
		if (merchant?.late_cancel_fee_type && merchant?.late_cancel_fee_value != null) {
			return `${formatLateCancelFee()} (same as late cancellation fee)`
		}

		return null
	}

	// Prepare customer info for Stripe
	const stripeCustomerInfo = {
		name: buildFullName(clientInfo.name, clientInfo.lastName),
		email: clientInfo.email,
		phone: clientInfo.phone
	}

	return (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			gap: '22px',
			width: '100%'
		}}>
			{/* Section Title */}
			<Typography sx={{
				fontFamily: 'Inter',
				fontWeight: 600,
				fontSize: isMobile ? '24px' : '28px',
				lineHeight: 1.29,
				color: '#2D3748'
			}}>
				Payment Information
			</Typography>

			{/* Cancellation & No-Show Policy */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '16px',
				padding: '16px',
				bgcolor: '#FEF3C7',
				border: '1px solid #F59E0B',
				borderRadius: '12px'
			}}>
				<Typography sx={{
					fontFamily: 'Inter',
					fontWeight: 600,
					fontSize: '16px',
					lineHeight: 1.5,
					color: '#92400E'
				}}>
					Cancellation & No-Show Policy
				</Typography>

				{/* Late Cancellation Policy */}
				<Box>
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: '14px',
						lineHeight: 1.5,
						color: '#2D3748',
						mb: 0.5
					}}>
						Late Cancellation:
					</Typography>
					{merchant?.late_cancel_hours && formatLateCancelFee() ? (
						<Typography sx={{
							fontFamily: 'Inter',
							fontWeight: 400,
							fontSize: '14px',
							lineHeight: 1.5,
							color: '#4A5568'
						}}>
							Cancellations within {merchant.late_cancel_hours} hours of your appointment will incur a fee of {formatLateCancelFee()}.
						</Typography>
					) : (
						<Typography sx={{
							fontFamily: 'Inter',
							fontWeight: 400,
							fontSize: '14px',
							lineHeight: 1.5,
							color: '#4A5568'
						}}>
							Free cancellation at any time before your appointment.
						</Typography>
					)}
				</Box>

				{/* No-Show Policy */}
				<Box>
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: '14px',
						lineHeight: 1.5,
						color: '#2D3748',
						mb: 0.5
					}}>
						No-Show:
					</Typography>
					{formatNoShowFee() ? (
						<Typography sx={{
							fontFamily: 'Inter',
							fontWeight: 400,
							fontSize: '14px',
							lineHeight: 1.5,
							color: '#4A5568'
						}}>
							If you don't show up for your appointment, a fee of {formatNoShowFee()} will be charged.
						</Typography>
					) : (
						<Typography sx={{
							fontFamily: 'Inter',
							fontWeight: 400,
							fontSize: '14px',
							lineHeight: 1.5,
							color: '#4A5568'
						}}>
							No fee will be charged if you don't show up.
						</Typography>
					)}
				</Box>
			</Box>

			{/* Payment Info Text */}
			<Typography sx={{
				fontFamily: 'Inter',
				fontWeight: 400,
				fontSize: '14px',
				lineHeight: 1.5,
				color: '#4A5568'
			}}>
				Please provide your payment information to secure your appointment. Your card will only be charged in case of late cancellation or no-show.
			</Typography>

			{/* Stripe Checkout */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '16px',
				padding: '16px',
				bgcolor: '#FFFFFF',
				border: '1px solid #CBD5E0',
				borderRadius: '12px',
				boxShadow: '0px 4px 4px -4px rgba(12, 12, 13, 0.05), 0px 16px 32px -4px rgba(12, 12, 13, 0.1)'
			}}>
			<StripeCheckout
				customerInfo={stripeCustomerInfo}
				servicePrice={calculateTotalPrice()}
				onSuccess={onPaymentSuccess}
				onError={onPaymentError}
				currencySymbol={getCurrencySymbol(merchant?.currency)}
			/>
			</Box>

			{/* Back Button */}
			<Button
				onClick={onBack}
				sx={{
					py: '12px',
					px: '24px',
					bgcolor: '#FFFFFF',
					border: '1px solid #CBD5E0',
					borderRadius: '12px',
					color: '#9F7AEA',
					fontFamily: 'Inter',
					fontWeight: 600,
					fontSize: '16px',
					lineHeight: 1.21,
					textTransform: 'none',
					alignSelf: 'flex-start',
					'&:hover': {
						bgcolor: '#F7FAFC'
					}
				}}
			>
				Back
			</Button>

			{/* Security Note */}
			<Box sx={{
				display: 'flex',
				alignItems: 'center',
				gap: '8px'
			}}>
				<Typography sx={{
					fontFamily: 'Inter',
					fontWeight: 400,
					fontSize: '12px',
					lineHeight: 1.5,
					color: '#718096'
				}}>
					🔒 Your payment information is securely processed by Stripe. We never store your full card details.
				</Typography>
			</Box>
		</Box>
	)
}

export default PaymentStep

